// Build a custom alarm (custom + conditional alarms, phase 5). One scrolling
// page rather than a wizard: pick a signal, say what sets it off, set the
// numbers, name it. Each section appears once the one above it is answered, so
// the page starts as a single question instead of a wall of empty inputs — but
// nothing is hidden behind a "next" button, and the review panel at the bottom
// is always there.
//
// Phase 6 adds the optional "Only when…" section between the numbers and the
// name — up to four condition rows, an all/any toggle, and a permanent note
// about what the boat does when a condition's signal goes quiet. It lives in
// `components/alarms/ConditionBuilder.tsx`; this page owns the draft and every
// live calculation the rows and the review both read, so the two can never
// disagree.
//
// Everything decision-shaped lives in pure modules: `customAlarmForm.ts` (draft,
// validation, POST body, live verdicts), `alarmSentence.ts` (the plain-English
// sentence and the prefilled name), `customAlarmConditions.ts` (everything the
// condition section says), `signalLive.ts` (which reading to show),
// `customAlarmErrors.ts` (which field a rejection belongs beside). This file is
// the shell over them.
//
// Two honesty rules shape the copy. A threshold is typed in ONE unit and the
// live reading beside it is printed in that same unit — the account's where a
// conversion applies (ADR 0189), the boat's otherwise; `customValueUnits` makes
// that decision once for the whole page and everything numeric here reads it.
// Storage never moves: the POST carries store units, resolved through the draft's
// baseline so a box nobody touched re-emits its original number. And the review
// states what the boat would do with the readings on screen right now ("this
// alarm would be firing"), naming the condition that decided it, and leaving the
// line out entirely when that cannot be decided rather than guessing.
//
// The CONDITION rows are the exception, deliberately: a clause's threshold is
// authored in the signal's own store unit on every surface, so its box, its chip
// and its reading stay there together.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import type { NotificationUrgency } from '@neomaris/rules';

import { api } from '../api';
import type { DeliveryState } from '../lib/alarmDisplay';
import { ConditionBuilder } from '../components/alarms/ConditionBuilder';
import { SignalPicker } from '../components/alarms/SignalPicker';
import { SeveritySelect, WatchScheduleField } from '../components/alarms/formFields';
import { useBoatChrome } from '../components/app/boatChrome';
import { conditionsClause, customAlarmSentence, defaultTitle } from '../lib/alarmSentence';
import {
  clauseExpectation,
  liveVerdictLine,
  shiftConditionTouched,
  type ClauseState,
} from '../lib/customAlarmConditions';
import { errorContextFor, mapCreateError, type CreateErrorView } from '../lib/customAlarmErrors';
import {
  CUSTOM_MODE_UNIVERSE,
  CUSTOM_TIMING_LABELS,
  DEFAULT_STALE_SECONDS,
  NOTIFY_CHOICES,
  addCondition,
  conditionField,
  conditionIndexOf,
  conditionSignals,
  customValueUnits,
  conditionVerdicts,
  draftConditions,
  draftThresholds,
  errorFor,
  hasErrors,
  initCustomDraft,
  notifyLabel,
  readDraftPrefill,
  removeCondition,
  setClearDelaySeconds,
  setClearpoint,
  setConditionLogic,
  setConditionOperator,
  setConditionSignal,
  setConditionValue,
  setEnabledModes,
  setNotify,
  setSetDelaySeconds,
  setSetpoint,
  setSeverity,
  setSignal,
  setStaleSeconds,
  setTitle,
  setTriggerClass,
  syncAutoTitle,
  toCreateBody,
  toggleMode,
  triggerHelp,
  triggerLabel,
  validate,
  wouldFireNow,
  type ConditionLogic,
  type ConditionOperator,
  type CustomAlarmDraft,
  type CustomAlarmField,
  type LiveReading,
} from '../lib/customAlarmForm';
import { formatRelativeShort } from '../lib/deviceSeen';
import {
  freshness,
  signalDisplayName,
  signalLabel,
  suggestedTriggerClasses,
  type Signal,
  type TriggerClass,
} from '../lib/signalCatalog';
import { formatReading, signalReading } from '../lib/signalLive';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { useUnits } from '../lib/useUnits';
import { useSignalCatalog } from '../lib/useSignalCatalog';

interface CreateResponse {
  id: string;
  compileStatus: string | null;
  rulesetVersion: number | null;
  delivery?: { state?: DeliveryState };
}

/** Re-render cadence for freshness wording and the live would-fire line. */
const TICK_MS = 30_000;

/** The human name for a catalog signal, device included when the boat named it. */
function labelOf(signal: Signal): string {
  return signalDisplayName(signal);
}

/**
 * How a condition row prints its signal's reading. "now 26.6 V" only for a
 * reading recent enough to say "now" about; an older one carries its age, so it
 * can never sit beside a "no reading" chip looking like a contradiction. A value
 * with no timestamp at all makes no claim about when.
 */
function readingHint(
  text: string | null,
  isFresh: boolean,
  relative: string | null
): string | null {
  if (text === null) return null;
  if (isFresh) return `now ${text}`;
  if (relative === null) return text;
  return `${text}, ${relative.toLowerCase()}`;
}

export function AlarmNewCustom() {
  const { boatId, boat, alerts, liveSamples } = useBoatChrome();
  const navigate = useNavigate();
  const location = useLocation();
  useDocumentTitle(
    boat ? `${boat.display_name} — Build a custom alarm` : 'Build a custom alarm — Neomaris'
  );
  // The account's display units (ADR 0189). They ride the DRAFT, which is what
  // lets the pure reducer read and write the value boxes in them without a React
  // context — and what makes the POST convert back exactly once.
  const units = useUnits();

  const catalog = useSignalCatalog(boatId);
  // The mirror of the boat's own "power or sail" answer (ADR 0173), or null when
  // unknown — the watch-schedule field's words depend on it.
  const vesselType =
    alerts.state.kind === 'ready' ? (alerts.state.response.vesselType ?? null) : null;
  // "Duplicate for <the other engine>" (plan D3) arrives as router state: the
  // same alarm, aimed at a sibling path. Read ONCE into the initial draft — a
  // prefill that re-applied on every render would fight every keystroke — and
  // read defensively, since router state is whatever is in `history.state`.
  // A PREFILL brings its own unit context across (`readDraftPrefill` carries the
  // settings, the pinned signal's store unit and the baselines), so it is taken
  // verbatim: injecting today's account onto a draft whose boxes were rendered
  // without one would read a store number as if it were the owner's. A fresh
  // draft gets the account's units, so the box the operator fills is read in
  // their own unit and converted once on the POST.
  const [draft, setDraft] = useState<CustomAlarmDraft>(
    () => readDraftPrefill(location.state) ?? initCustomDraft(units)
  );
  const [touched, setTouched] = useState<ReadonlySet<CustomAlarmField>>(() => new Set());
  const [serverError, setServerError] = useState<CreateErrorView | null>(null);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), TICK_MS);
    return () => window.clearInterval(id);
  }, []);

  const backToAlarms = `/dashboard/boats/${boatId}/alarms`;

  const signal = useMemo(
    () => catalog.signals.find((s) => s.path === draft.signalPath),
    [catalog.signals, draft.signalPath]
  );
  const reading = useMemo(
    () => (signal === undefined ? null : signalReading(signal, liveSamples?.get(signal.path))),
    [signal, liveSamples]
  );
  // How the value boxes read, decided ONCE for this draft — the account's unit
  // where a conversion applies, the signal's own store unit otherwise. Every
  // number on this page that describes the trigger comes from here: the box, the
  // chip beside it, the "now …" reading under it and the review sentence.
  const boxUnits = useMemo(() => customValueUnits(draft), [draft]);
  const readingText =
    reading === null ? null : formatReading(reading, boxUnits.category, boxUnits.settings);

  const errors = useMemo(() => validate(draft, catalog.signals), [draft, catalog.signals]);
  const thresholds = useMemo(() => draftThresholds(draft), [draft]);

  // --- "Only when…" ---------------------------------------------------------
  // A clause can only be made from a number or a switch, so the row pickers see
  // a narrowed catalog — offering a text signal and then refusing every operator
  // for it reads as a broken form.
  const clauseCatalog = useMemo(() => conditionSignals(catalog.signals), [catalog.signals]);

  const signalAt = useCallback(
    (path: string | undefined): Signal | undefined =>
      path === undefined || path.length === 0
        ? undefined
        : catalog.signals.find((s) => s.path === path),
    [catalog.signals]
  );

  /**
   * The current reading for any catalog path, resolved by the same live-vs-stored
   * rule the trigger's own hint uses — so a clause chip can never disagree with
   * the value the picker row shows for the same signal.
   */
  const readingFor = useCallback(
    (path: string): LiveReading | undefined => {
      const found = signalAt(path);
      if (found === undefined) return undefined;
      const resolved = signalReading(found, liveSamples?.get(found.path));
      return { value: resolved.value, receivedAt: resolved.atMs ?? undefined };
    },
    [signalAt, liveSamples]
  );

  const clauseVerdicts = useMemo(
    () => conditionVerdicts(draft, readingFor, now),
    [draft, readingFor, now]
  );

  /**
   * Per row: its signal's name, unit and reading. Two forms of the reading,
   * because they answer different questions — the bare value for the review
   * line's prose, and a worded hint for the row itself.
   *
   * The hint carries the age whenever the value is too old to judge the clause
   * on. Printing "now 12.4 V" beside a chip reading "no reading" would be a
   * flat contradiction, and the age is exactly what explains it.
   */
  const clauseViews = useMemo(
    () =>
      draft.conditions.map((row) => {
        const rowSignal = signalAt(row.signalPath);
        const rowReading =
          rowSignal === undefined
            ? null
            : signalReading(rowSignal, liveSamples?.get(rowSignal.path));
        const readingText = rowReading === null ? null : formatReading(rowReading);
        const relative =
          rowReading === null || rowReading.atIso === null
            ? null
            : formatRelativeShort(rowReading.atIso, now);
        const isFresh = rowReading !== null && freshness(rowReading.atIso, now) === 'fresh';
        return {
          label: rowSignal === undefined ? '' : labelOf(rowSignal),
          unit: rowSignal?.unit ?? null,
          readingText,
          readingHint: readingHint(readingText, isFresh, relative),
        };
      }),
    [draft.conditions, signalAt, liveSamples, now]
  );

  const clauseStates = useMemo<ClauseState[]>(
    () =>
      draft.conditions.map((row, index) => ({
        label: clauseViews[index]?.label ?? '',
        verdict: clauseVerdicts[index] ?? null,
        expectation: clauseExpectation(row, clauseViews[index]?.unit),
        readingText: clauseViews[index]?.readingText ?? null,
      })),
    [draft.conditions, clauseViews, clauseVerdicts]
  );

  /** "only while Engine 1 revolutions is above 500 and shore power is off". */
  const conditionClause = useMemo(
    () =>
      conditionsClause(draftConditions(draft), {
        labelFor: (path) => {
          const found = signalAt(path);
          return found === undefined ? signalLabel(path) : labelOf(found);
        },
        unitFor: (path) => signalAt(path)?.unit ?? null,
      }),
    [draft, signalAt]
  );

  // The review sentence — and the name prefilled from it — need both a signal
  // and a trigger class; before that there is nothing honest to say.
  const sentence =
    signal !== undefined && draft.triggerClass !== undefined
      ? customAlarmSentence({ triggerClass: draft.triggerClass, path: signal.path }, thresholds, {
          label: labelOf(signal),
          // The STORE unit: `draftThresholds` is store-domain, and it is
          // `category` + `units` that restate it.
          unit: signal.unit,
          ...(boxUnits.category === undefined ? {} : { category: boxUnits.category }),
          ...(boxUnits.settings === undefined ? {} : { units: boxUnits.settings }),
          conditionClause,
        })
      : null;
  // The NAME deliberately stops at the trigger. It is what a list row, an email
  // subject and the boat's own display show, and folding four conditions into it
  // would push the part that identifies the alarm off the end of all three.
  const suggestion =
    signal !== undefined && draft.triggerClass !== undefined
      ? defaultTitle({ triggerClass: draft.triggerClass, path: signal.path }, thresholds, {
          label: labelOf(signal),
          unit: signal.unit,
          ...(boxUnits.category === undefined ? {} : { category: boxUnits.category }),
          ...(boxUnits.settings === undefined ? {} : { units: boxUnits.settings }),
        })
      : null;

  // What the review reads before there is a sentence: name the step that is
  // actually outstanding, because asking for a signal that has already been
  // picked reads as the page not having noticed.
  const placeholderSentence =
    signal === undefined
      ? 'Pick a signal and choose what sets it off.'
      : 'Now choose what sets it off.';

  useEffect(() => {
    if (suggestion === null || suggestion.length === 0) return;
    setDraft((d) => syncAutoTitle(d, suggestion));
  }, [suggestion]);

  const wouldFire = useMemo(
    () =>
      reading === null
        ? null
        : wouldFireNow(draft, { value: reading.value, receivedAt: reading.atMs ?? undefined }, now),
    [draft, reading, now]
  );

  /**
   * The combined live line: the trigger AND the conditions, folded the way the
   * boat folds them, and naming the condition that decided it. `null` means
   * there is nothing honest to say — the line is left out rather than guessed.
   */
  const verdict = useMemo(
    () =>
      liveVerdictLine({
        trigger: wouldFire,
        triggerReadingText: readingText,
        clauses: clauseStates,
        logic: draft.conditionLogic,
      }),
    [wouldFire, readingText, clauseStates, draft.conditionLogic]
  );

  // A signal that has gone quiet still binds, but it will not evaluate until it
  // reports — say so rather than letting a green-looking form imply otherwise.
  const quietNote = useMemo(() => {
    if (reading === null) return null;
    if (freshness(reading.atIso, now) === 'fresh') return null;
    const relative = reading.atIso === null ? null : formatRelativeShort(reading.atIso, now);
    if (relative === null) {
      return 'Your boat reports this signal but has not sent a value yet — it will read as pending until it does.';
    }
    return `Last reported ${relative.toLowerCase()} — it will read as pending until it reports again.`;
  }, [reading, now]);

  function touch(field: CustomAlarmField): void {
    setTouched((prev) => {
      if (prev.has(field)) return prev;
      const next = new Set(prev);
      next.add(field);
      return next;
    });
  }

  /** Apply a draft change, mark the field touched, and drop any stale server error. */
  function apply(next: CustomAlarmDraft, field?: CustomAlarmField): void {
    setDraft(next);
    if (field !== undefined) touch(field);
    if (serverError !== null) setServerError(null);
  }

  /**
   * Leaving the name empty hands the suggestion back, ON BLUR rather than on
   * every keystroke — refilling the box the instant it is cleared would fight
   * an operator who cleared it in order to type their own name.
   */
  function restoreSuggestedTitle(): void {
    touch('title');
    if (draft.title.trim().length > 0) return;
    if (suggestion === null || suggestion.length === 0) return;
    setDraft((d) => syncAutoTitle(d, suggestion));
  }

  /**
   * The message to show beside a field: a server rejection aimed at it always
   * shows; a client validation error shows only once the operator has touched
   * that field, so an untouched form does not shout.
   */
  function fieldError(field: CustomAlarmField): string | undefined {
    if (serverError?.field === field) return serverError.message;
    if (!touched.has(field)) return undefined;
    return errorFor(errors, field);
  }

  const firstError = errors[0];
  const blocked = hasErrors(errors);
  const formError = serverError !== null && serverError.field === undefined ? serverError : null;

  // The message beside a disabled Save. A condition row's complaint reads the
  // same for every row ("Enter a number."), so it is useless down here without
  // the row number attached to it.
  const blockedMessage = useMemo(() => {
    if (firstError === undefined) return undefined;
    const row = conditionIndexOf(firstError.field);
    return row === null ? firstError.message : `Condition ${row + 1}: ${firstError.message}`;
  }, [firstError]);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (busy || blocked) return;
    const body = toCreateBody(draft);
    if (body === null) return;
    setBusy(true);
    setServerError(null);
    try {
      const res = await api<CreateResponse>(`/v1/boats/${boatId}/alerts`, {
        method: 'POST',
        body,
        idempotencyKey: crypto.randomUUID(),
      });
      // Refresh the shared snapshot so the detail page finds the new definition,
      // then hand over the honest just-saved outcome.
      alerts.refresh();
      navigate(`${backToAlarms}/${res.id}`, {
        state: {
          justSaved: {
            kind: 'created',
            compileStatus: res.compileStatus,
            rulesetVersion: res.rulesetVersion,
            // Whether the boat actually GOT it — the create path needs this as
            // much as the edit path does (ADR 0179).
            delivery: res.delivery?.state ?? null,
          },
        },
      });
    } catch (err) {
      // The context is what lets a 422 naming one path land on the condition
      // row that pinned it rather than always on the trigger's picker.
      const mapped = mapCreateError(err, errorContextFor(draft));
      setServerError(mapped);
      if (mapped.field !== undefined) touch(mapped.field);
      // The 422 means the catalog on screen is out of date — refetch it so the
      // picker stops offering a signal the boat has stopped reporting.
      if (mapped.staleCatalog === true) catalog.refresh();
    } finally {
      setBusy(false);
    }
  }

  const classes: readonly TriggerClass[] =
    signal === undefined ? [] : suggestedTriggerClasses(signal.kind, signal.path);
  // The chip beside the value boxes — the unit those boxes are actually in, not
  // the boat's, so the label and the number can never come from two places.
  const unit =
    signal === undefined
      ? null
      : boxUnits.unitSuffix.length > 0
        ? boxUnits.unitSuffix
        : (signal.unit ?? null);

  function pickSignal(picked: Signal): void {
    apply(setSignal(draft, picked), 'signalPath');
  }

  /**
   * Dropping a row renumbers every row above it, so the touched set — which is
   * keyed by row POSITION — is shifted with it. Without that, row 3's "enter a
   * number" appears beside row 2 the moment row 1 is removed.
   */
  function removeConditionRow(index: number): void {
    setTouched((prev) => shiftConditionTouched(prev, index));
    apply(removeCondition(draft, index));
  }

  return (
    <section data-testid="alarm-new-custom">
      <Link className="detail-back" to={`${backToAlarms}/new`}>
        ← Back to the alarm library
      </Link>

      <div className="page-head">
        <h1>Build a custom alarm</h1>
        <p className="page-sub">
          Watch anything your boat reports, on your own numbers. Values are shown and typed in your
          units while your boat keeps its own — the current reading is beside every box.
        </p>
      </div>

      {catalog.status === 'loading' ? (
        <div className="alarm-custom-state" aria-busy="true" data-testid="alarm-custom-loading">
          <p className="alarm-custom-state__line">Reading what your boat reports…</p>
          <div className="alarm-custom-skeleton" aria-hidden="true">
            <span />
            <span />
            <span />
          </div>
        </div>
      ) : null}

      {catalog.status === 'error' ? (
        <div className="alarm-custom-state" role="alert" data-testid="alarm-custom-catalog-error">
          <p className="alarm-custom-state__line">
            We couldn’t load the list of signals for this boat.
          </p>
          <p className="muted">{catalog.error}</p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={catalog.refresh}
            disabled={catalog.refreshing}
            data-testid="alarm-custom-catalog-retry"
          >
            {catalog.refreshing ? 'Checking…' : 'Try again'}
          </button>
        </div>
      ) : null}

      {catalog.status === 'ready' && catalog.signals.length === 0 ? (
        <div className="alarm-custom-state" data-testid="alarm-custom-catalog-empty">
          <p className="alarm-custom-state__line">
            This boat has not reported any signals yet, so there is nothing to build an alarm on.
          </p>
          <p className="muted">
            Signals appear here once your boat sends data from its network. Once it has, come back
            and this list will fill in on its own.
          </p>
          <div className="alarm-custom-state__actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={catalog.refresh}
              disabled={catalog.refreshing}
              data-testid="alarm-custom-catalog-recheck"
            >
              {catalog.refreshing ? 'Checking…' : 'Check again'}
            </button>
            <Link className="btn" to={`/dashboard/boats/${boatId}/devices`}>
              See your devices
            </Link>
          </div>
        </div>
      ) : null}

      {catalog.status === 'ready' && catalog.signals.length > 0 ? (
        <form
          className="alarm-form alarm-custom-form"
          data-testid="alarm-custom-form"
          onSubmit={(e) => void submit(e)}
          noValidate
        >
          {/* 1 — the signal */}
          <fieldset className="alarm-form-fieldset">
            <legend>What should the boat watch?</legend>
            <SignalPicker
              signals={catalog.signals}
              value={draft.signalPath}
              onPick={pickSignal}
              label="Signal"
              testidPrefix="signal-picker"
            />
            {fieldError('signalPath') !== undefined ? (
              <p className="alarm-form-err" data-testid="alarm-custom-signal-err">
                {fieldError('signalPath')}
              </p>
            ) : null}
            {quietNote !== null ? (
              <p className="alarm-custom-note" data-testid="alarm-custom-quiet-note">
                {quietNote}
              </p>
            ) : null}
          </fieldset>

          {/* 2 — what sets it off */}
          {signal !== undefined ? (
            <fieldset className="alarm-form-fieldset">
              <legend>What sets it off?</legend>
              <div className="alarm-custom-classes" role="radiogroup" aria-label="What sets it off">
                {classes.map((cls) => (
                  <label
                    key={cls}
                    className={`alarm-custom-class${draft.triggerClass === cls ? ' alarm-custom-class--on' : ''}`}
                  >
                    <input
                      type="radio"
                      name="alarm-custom-class"
                      value={cls}
                      checked={draft.triggerClass === cls}
                      data-testid={`alarm-custom-class-${cls}`}
                      onChange={() => apply(setTriggerClass(draft, cls), 'triggerClass')}
                    />
                    <span className="alarm-custom-class__body">
                      <span className="alarm-custom-class__title">{triggerLabel(cls)}</span>
                      <span className="alarm-custom-class__sub">{triggerHelp(cls)}</span>
                    </span>
                  </label>
                ))}
              </div>
              {fieldError('triggerClass') !== undefined ? (
                <p className="alarm-form-err">{fieldError('triggerClass')}</p>
              ) : null}
            </fieldset>
          ) : null}

          {/* 3 — the numbers */}
          {signal !== undefined && draft.triggerClass !== undefined ? (
            <fieldset className="alarm-form-fieldset">
              <legend>
                {draft.triggerClass === 'DataUnavailable' ? 'How long to wait' : 'The numbers'}
              </legend>

              {draft.triggerClass === 'High' || draft.triggerClass === 'Low' ? (
                <div className="alarm-custom-field">
                  <label htmlFor="alarm-custom-setpoint">
                    {draft.triggerClass === 'High' ? 'Set off above' : 'Set off below'}
                  </label>
                  <div className="alarm-custom-row">
                    <input
                      id="alarm-custom-setpoint"
                      className="form-input"
                      type="number"
                      inputMode="decimal"
                      step="any"
                      value={draft.setpoint ?? ''}
                      data-testid="alarm-custom-setpoint"
                      onChange={(e) => apply(setSetpoint(draft, e.target.value), 'setpoint')}
                      onBlur={() => touch('setpoint')}
                    />
                    {unit === null || unit === '' ? null : (
                      <span className="alarm-form-unit">{unit}</span>
                    )}
                    {readingText !== null ? (
                      <span className="alarm-custom-now" data-testid="alarm-custom-now">
                        now {readingText}
                      </span>
                    ) : null}
                  </div>
                  {fieldError('setpoint') !== undefined ? (
                    <p className="alarm-form-err">{fieldError('setpoint')}</p>
                  ) : null}
                </div>
              ) : null}

              {draft.triggerClass === 'DataUnavailable' ? (
                <div className="alarm-custom-field">
                  <label htmlFor="alarm-custom-stale">{CUSTOM_TIMING_LABELS.stale}</label>
                  <div className="alarm-custom-row">
                    <input
                      id="alarm-custom-stale"
                      className="form-input"
                      type="number"
                      inputMode="numeric"
                      step="1"
                      min="0"
                      value={draft.staleSeconds ?? ''}
                      data-testid="alarm-custom-stale"
                      onChange={(e) =>
                        apply(setStaleSeconds(draft, e.target.value), 'staleSeconds')
                      }
                      onBlur={() => touch('staleSeconds')}
                    />
                    <span className="alarm-form-unit">seconds</span>
                  </div>
                  <p className="alarm-form-hint">
                    Starts at {DEFAULT_STALE_SECONDS} seconds (5 minutes) — the same wait your boat
                    uses if you leave this empty.
                  </p>
                  {fieldError('staleSeconds') !== undefined ? (
                    <p className="alarm-form-err">{fieldError('staleSeconds')}</p>
                  ) : null}
                </div>
              ) : (
                <details
                  className="alarm-custom-advanced"
                  // A server rejection aimed at a field inside this disclosure
                  // must never land invisibly behind a collapsed summary.
                  open={
                    (serverError !== null &&
                      (serverError.field === 'clearpoint' ||
                        serverError.field === 'setDelaySeconds' ||
                        serverError.field === 'clearDelaySeconds')) ||
                    undefined
                  }
                >
                  <summary>Fine tuning</summary>

                  {draft.triggerClass === 'High' || draft.triggerClass === 'Low' ? (
                    <div className="alarm-custom-field">
                      <label htmlFor="alarm-custom-clearpoint">Clears at</label>
                      <div className="alarm-custom-row">
                        <input
                          id="alarm-custom-clearpoint"
                          className="form-input"
                          type="number"
                          inputMode="decimal"
                          step="any"
                          value={draft.clearpoint ?? ''}
                          data-testid="alarm-custom-clearpoint"
                          onChange={(e) =>
                            apply(setClearpoint(draft, e.target.value), 'clearpoint')
                          }
                          onBlur={() => touch('clearpoint')}
                        />
                        {unit === null || unit === '' ? null : (
                          <span className="alarm-form-unit">{unit}</span>
                        )}
                      </div>
                      <p className="alarm-form-hint">
                        Left blank, it clears at the set point — it may chatter near the line.
                      </p>
                      {fieldError('clearpoint') !== undefined ? (
                        <p className="alarm-form-err">{fieldError('clearpoint')}</p>
                      ) : null}
                    </div>
                  ) : null}

                  <div className="alarm-custom-field">
                    <label htmlFor="alarm-custom-setdelay">{CUSTOM_TIMING_LABELS.setDelay}</label>
                    <div className="alarm-custom-row">
                      <input
                        id="alarm-custom-setdelay"
                        className="form-input"
                        type="number"
                        inputMode="numeric"
                        step="1"
                        min="0"
                        value={draft.setDelaySeconds}
                        data-testid="alarm-custom-setdelay"
                        onChange={(e) =>
                          apply(setSetDelaySeconds(draft, e.target.value), 'setDelaySeconds')
                        }
                        onBlur={() => touch('setDelaySeconds')}
                      />
                      <span className="alarm-form-unit">seconds</span>
                    </div>
                    <p className="alarm-form-hint">
                      Ride out a brief spike. Zero sets the alarm off on the first reading past the
                      line.
                    </p>
                    {fieldError('setDelaySeconds') !== undefined ? (
                      <p className="alarm-form-err">{fieldError('setDelaySeconds')}</p>
                    ) : null}
                  </div>

                  <div className="alarm-custom-field">
                    <label htmlFor="alarm-custom-cleardelay">
                      {CUSTOM_TIMING_LABELS.clearDelay}
                    </label>
                    <div className="alarm-custom-row">
                      <input
                        id="alarm-custom-cleardelay"
                        className="form-input"
                        type="number"
                        inputMode="numeric"
                        step="1"
                        min="0"
                        value={draft.clearDelaySeconds}
                        data-testid="alarm-custom-cleardelay"
                        onChange={(e) =>
                          apply(setClearDelaySeconds(draft, e.target.value), 'clearDelaySeconds')
                        }
                        onBlur={() => touch('clearDelaySeconds')}
                      />
                      <span className="alarm-form-unit">seconds</span>
                    </div>
                    <p className="alarm-form-hint">
                      How long the reading must stay back on the safe side before the alarm goes
                      away.
                    </p>
                    {fieldError('clearDelaySeconds') !== undefined ? (
                      <p className="alarm-form-err">{fieldError('clearDelaySeconds')}</p>
                    ) : null}
                  </div>
                </details>
              )}
            </fieldset>
          ) : null}

          {/* 4 — only when… (optional; closed and empty until asked for) */}
          {signal !== undefined && draft.triggerClass !== undefined ? (
            <ConditionBuilder
              signals={clauseCatalog}
              rows={draft.conditions}
              logic={draft.conditionLogic}
              verdicts={clauseVerdicts}
              readingHints={clauseViews.map((view) => view.readingHint)}
              errors={draft.conditions.map((_, index) => fieldError(conditionField(index)))}
              sectionError={fieldError('conditions')}
              onAdd={() => apply(addCondition(draft))}
              onRemove={removeConditionRow}
              onPickSignal={(index, picked) =>
                apply(setConditionSignal(draft, index, picked), conditionField(index))
              }
              onOperator={(index, operator: ConditionOperator) =>
                apply(setConditionOperator(draft, index, operator), conditionField(index))
              }
              onValue={(index, value) =>
                apply(setConditionValue(draft, index, value), conditionField(index))
              }
              onLogic={(logic: ConditionLogic) => apply(setConditionLogic(draft, logic))}
            />
          ) : null}

          {/* 5 — name it, and say how loud it should be */}
          {signal !== undefined && draft.triggerClass !== undefined ? (
            <fieldset className="alarm-form-fieldset">
              <legend>Name it</legend>

              <div className="form-group">
                <label htmlFor="alarm-custom-title">Name</label>
                <input
                  id="alarm-custom-title"
                  className="form-input"
                  type="text"
                  value={draft.title}
                  data-testid="alarm-custom-title"
                  onChange={(e) => apply(setTitle(draft, e.target.value), 'title')}
                  onBlur={restoreSuggestedTitle}
                />
                <p className="alarm-form-hint">
                  We keep this in step with what sets the alarm off, until you write your own. Empty
                  it and we put the suggestion back.
                </p>
                {fieldError('title') !== undefined ? (
                  <p className="alarm-form-err">{fieldError('title')}</p>
                ) : null}
              </div>

              <SeveritySelect
                value={draft.severity}
                onChange={(severity) => apply(setSeverity(draft, severity))}
                id="alarm-custom-severity"
                testId="alarm-custom-severity"
                label="How serious is it?"
              />

              <WatchScheduleField
                name="alarm-custom-watch"
                modeUniverse={CUSTOM_MODE_UNIVERSE}
                enabledModes={draft.enabledModes}
                onSetModes={(modes) => apply(setEnabledModes(draft, modes), 'enabledModes')}
                onToggleMode={(m) => apply(toggleMode(draft, m), 'enabledModes')}
                error={errorFor(errors, 'enabledModes')}
                testIdPrefix="alarm-custom-mode-"
                presetTestIdPrefix="alarm-custom-watch-"
                vesselType={vesselType}
              />

              <div className="form-group">
                <label htmlFor="alarm-custom-notify">When it goes off</label>
                <select
                  id="alarm-custom-notify"
                  className="form-input"
                  value={draft.notify}
                  data-testid="alarm-custom-notify"
                  onChange={(e) => apply(setNotify(draft, e.target.value as NotificationUrgency))}
                >
                  {NOTIFY_CHOICES.map((urgency) => (
                    <option key={urgency} value={urgency}>
                      {notifyLabel(urgency)}
                    </option>
                  ))}
                </select>
              </div>
            </fieldset>
          ) : null}

          {/* Always visible: what this will do, and whether it would fire now. */}
          <div className="alarm-custom-review" data-testid="alarm-custom-review">
            <h2 className="alarm-custom-review__head">What this alarm will do</h2>
            <p className="alarm-custom-sentence" data-testid="alarm-custom-sentence">
              {sentence ?? placeholderSentence}
            </p>
            {verdict !== null ? (
              <p
                className={`alarm-custom-wouldfire alarm-custom-wouldfire--${verdict.tone}`}
                data-testid="alarm-custom-wouldfire"
              >
                {verdict.lead} <strong>{verdict.verdict}</strong>
                {verdict.reason === undefined ? '.' : ` — ${verdict.reason}.`}
              </p>
            ) : null}

            {formError !== null ? (
              <p className="alarm-command-banner" role="alert" data-testid="alarm-custom-error">
                {formError.message}
              </p>
            ) : null}

            <div className="alarm-form-actions">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={busy || blocked}
                aria-describedby={
                  blocked && blockedMessage !== undefined ? 'alarm-custom-blocked' : undefined
                }
                data-testid="alarm-custom-submit"
              >
                {busy ? 'Saving…' : 'Save alarm'}
              </button>
              <Link className="btn" to={`${backToAlarms}/new`}>
                Cancel
              </Link>
              {blocked && blockedMessage !== undefined ? (
                <span
                  className="alarm-custom-blocked"
                  id="alarm-custom-blocked"
                  data-testid="alarm-custom-blocked"
                >
                  {blockedMessage}
                </span>
              ) : null}
            </div>
          </div>
        </form>
      ) : null}
    </section>
  );
}
